/**
 * Infinity Collectables – Cart Drawer
 * assets/ic-cart-drawer.js
 *
 * - Opens on Add-to-Cart (any form with data-type="add-to-cart-form")
 * - Opens on overlay "Add to basket" buttons (data-ic-overlay-add)
 * - Qty +/- and remove buttons update cart via Fetch API
 * - Tracks originating collection URL for "Continue shopping" link
 * - Updates free-shipping progress bar live
 * - Emits 'cart:updated' event for other components (shipping bar)
 */

(function () {
  'use strict';

  var drawer, backdrop, panel, closeBtn, continueLink;
  var lastCollectionUrl = document.referrer || '/collections/all';

  // ── Helpers ─────────────────────────────────────────────────────────────────

  function money(cents) {
    var val = (cents / 100).toFixed(2).replace(/\.00$/, '');
    return '£' + val;
  }

  function openDrawer() {
    drawer.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    panel.focus();
    // Update continue link with originating page
    if (continueLink) {
      var ref = window.location.pathname;
      if (ref.indexOf('/collections/') !== -1 || ref.indexOf('/products/') !== -1) {
        lastCollectionUrl = ref;
      }
      continueLink.href = lastCollectionUrl;
    }
  }

  function closeDrawer() {
    drawer.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = '';
  }

  // ── Update drawer UI from cart data ─────────────────────────────────────────

  function updateDrawer(cart) {
    // Item count
    var countEl = document.getElementById('ic-drawer-count');
    if (countEl) countEl.textContent = '(' + cart.item_count + ')';

    // Shipping bar
    var shipBarEl = document.getElementById('ic-drawer-ship-bar');
    var threshold = shipBarEl ? parseInt(shipBarEl.dataset.threshold, 10) : 5000;
    var fill = document.getElementById('ic-drawer-ship-fill');
    var shipMsg = document.getElementById('ic-drawer-ship-msg');
    var shipRemaining = document.getElementById('ic-drawer-ship-remaining');
    var pct = Math.min(100, (cart.total_price / threshold) * 100);

    if (fill) fill.style.width = pct + '%';

    if (cart.total_price >= threshold) {
      if (fill) fill.classList.add('ic-cart-drawer__ship-fill--done');
      if (shipMsg) {
        shipMsg.innerHTML = '🎉 You\'ve unlocked free shipping!';
        shipMsg.classList.add('ic-cart-drawer__ship-msg--done');
      }
    } else {
      if (fill) fill.classList.remove('ic-cart-drawer__ship-fill--done');
      if (shipMsg) shipMsg.classList.remove('ic-cart-drawer__ship-msg--done');
      if (shipRemaining) {
        shipRemaining.textContent = money(threshold - cart.total_price);
      }
    }

    // Subtotal
    var subtotalEl = document.getElementById('ic-drawer-subtotal');
    if (subtotalEl) subtotalEl.textContent = money(cart.total_price);

    // Checkout button total
    var checkoutBtn = drawer.querySelector('.ic-cart-drawer__checkout');
    if (checkoutBtn) checkoutBtn.textContent = '';
    if (checkoutBtn) {
      checkoutBtn.innerHTML = 'Checkout — ' + money(cart.total_price) +
        '<svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M5 12h14M12 5l7 7-7 7"/></svg>';
    }

    // Savings
    var totalSavings = 0;
    cart.items.forEach(function (item) {
      if (item.original_price > item.final_price) {
        totalSavings += (item.original_price - item.final_price) * item.quantity;
      }
    });
    var savingsEl = document.getElementById('ic-drawer-savings');
    if (totalSavings > 0) {
      if (!savingsEl) {
        savingsEl = document.createElement('div');
        savingsEl.id = 'ic-drawer-savings';
        savingsEl.className = 'ic-cart-drawer__savings';
        var footer = drawer.querySelector('.ic-cart-drawer__footer');
        if (footer) footer.insertBefore(savingsEl, footer.firstChild);
      }
      savingsEl.innerHTML = '🎁 You\'re saving <strong>' + money(totalSavings) + '</strong> on this order!';
    } else if (savingsEl) {
      savingsEl.remove();
    }

    // Rebuild items HTML
    var itemsEl = document.getElementById('ic-drawer-items');
    if (!itemsEl) return;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '<div class="ic-cart-drawer__empty">' +
        '<p>Your basket is empty</p>' +
        '<a href="/collections/all" class="ic-cart-drawer__continue">Browse products</a></div>';
    } else {
      var html = '';
      cart.items.forEach(function (item) {
        var saving = item.original_price > item.final_price
          ? (item.original_price - item.final_price)
          : 0;
        var imgSrc = item.image
          ? item.image.replace(/\.(jpg|jpeg|png|webp|gif)/i, '_120x120.$1')
          : 'https://cdn.shopify.com/s/images/admin/no-image-medium.gif';

        html += '<div class="ic-cart-drawer__item" data-item-key="' + item.key + '">' +
          '<a href="' + item.url + '" class="ic-cart-drawer__item-img-wrap">' +
            '<img src="' + imgSrc + '" alt="' + (item.title || '').replace(/"/g, '&quot;') + '" width="80" height="80" loading="lazy" class="ic-cart-drawer__item-img">' +
          '</a>' +
          '<div class="ic-cart-drawer__item-details">' +
            '<a href="' + item.url + '" class="ic-cart-drawer__item-title">' + item.product_title + '</a>';

        if (item.variant_title && item.variant_title !== 'Default Title') {
          html += '<span class="ic-cart-drawer__item-variant">' + item.variant_title + '</span>';
        }

        html += '<div class="ic-cart-drawer__item-pricing">';
        if (saving > 0) {
          html += '<span class="ic-cart-drawer__item-price ic-cart-drawer__item-price--sale">' + money(item.final_price) + '</span>' +
                  '<span class="ic-cart-drawer__item-was">was ' + money(item.original_price) + '</span>' +
                  '<span class="ic-cart-drawer__item-save">Save ' + money(saving) + '/ea</span>';
        } else {
          html += '<span class="ic-cart-drawer__item-price">' + money(item.final_price) + '</span>';
        }
        html += '</div>';

        html += '<div class="ic-cart-drawer__item-qty-row">' +
          '<div class="ic-cart-drawer__qty">' +
            '<button type="button" class="ic-cart-drawer__qty-btn" data-action="decrease" data-key="' + item.key + '" aria-label="Remove one">−</button>' +
            '<span class="ic-cart-drawer__qty-num" data-qty="' + item.quantity + '">' + item.quantity + '</span>' +
            '<button type="button" class="ic-cart-drawer__qty-btn" data-action="increase" data-key="' + item.key + '" aria-label="Add one">+</button>' +
          '</div>' +
          '<button type="button" class="ic-cart-drawer__remove" data-key="' + item.key + '" aria-label="Remove">' +
            '<svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6"/><path d="M10 11v6M14 11v6"/><path d="M9 6V4h6v2"/></svg>' +
            ' Remove' +
          '</button>' +
        '</div>' +
        '</div>' + // item-details
        '</div>'; // item
      });
      itemsEl.innerHTML = html;
    }

    // Emit global event for other components
    document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart: cart } }));
  }

  // ── Cart API calls ───────────────────────────────────────────────────────────

  function fetchCart() {
    return fetch('/cart.js', { headers: { 'Content-Type': 'application/json' } })
      .then(function (r) { return r.json(); });
  }

  function addToCart(variantId, quantity) {
    return fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ items: [{ id: variantId, quantity: quantity || 1 }] })
    }).then(function (r) { return r.json(); });
  }

  function changeQty(key, quantity) {
    drawer.classList.add('ic-cart-drawer--loading');
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: key, quantity: quantity })
    })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        drawer.classList.remove('ic-cart-drawer--loading');
        updateDrawer(cart);
        return cart;
      })
      .catch(function () { drawer.classList.remove('ic-cart-drawer--loading'); });
  }

  // ── Event listeners ──────────────────────────────────────────────────────────

  function bindDrawerEvents() {
    // Close button
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);

    // Backdrop click
    if (backdrop) backdrop.addEventListener('click', closeDrawer);

    // Escape key
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && drawer.getAttribute('aria-hidden') === 'false') {
        closeDrawer();
      }
    });

    // Qty + / – and remove
    drawer.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-action]');
      if (btn) {
        e.preventDefault();
        var key = btn.dataset.key;
        var qtyEl = btn.closest('.ic-cart-drawer__qty') &&
                    btn.closest('.ic-cart-drawer__qty').querySelector('.ic-cart-drawer__qty-num');
        var currentQty = qtyEl ? parseInt(qtyEl.dataset.qty, 10) : 1;
        var newQty = btn.dataset.action === 'increase' ? currentQty + 1 : currentQty - 1;
        changeQty(key, Math.max(0, newQty));
        return;
      }

      var removeBtn = e.target.closest('.ic-cart-drawer__remove');
      if (removeBtn) {
        e.preventDefault();
        changeQty(removeBtn.dataset.key, 0);
      }
    });
  }

  // ── Intercept Add-to-Cart forms ─────────────────────────────────────────────

  function interceptAddToCartForms() {
    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || form.dataset.type !== 'add-to-cart-form') return;

      var variantInput = form.querySelector('[name="id"]');
      if (!variantInput) return;

      var variantId = variantInput.value;
      var qtyInput = form.querySelector('[name="quantity"]');
      var qty = qtyInput ? parseInt(qtyInput.value, 10) : 1;

      // Let the native form handle it but also update drawer
      // We override only if Shopify's product-form.js is active; fall back gracefully
      // We won't preventDefault here — Shopify's own JS handles the cart update.
      // Instead we listen for the 'cart:updated' event or poll after a short delay.
      setTimeout(function () {
        fetchCart().then(function (cart) {
          updateDrawer(cart);
          openDrawer();
        });
      }, 600);
    });
  }

  // ── Intercept overlay "Add to basket" buttons ───────────────────────────────

  function interceptOverlayButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('[data-ic-overlay-add]');
      if (!btn) return;
      e.preventDefault();

      var variantId = btn.dataset.variantId;
      if (!variantId) return;

      btn.textContent = '…';
      addToCart(variantId, 1).then(function () {
        return fetchCart();
      }).then(function (cart) {
        btn.textContent = '✓ Added';
        setTimeout(function () { btn.textContent = 'Add to basket'; }, 2000);
        updateDrawer(cart);
        openDrawer();
      }).catch(function () {
        btn.textContent = 'Error – try again';
      });
    });
  }

  // ── Listen for native Shopify cart updates ──────────────────────────────────

  function listenForShopifyEvents() {
    // Shopify's product-form.js dispatches these on window
    document.addEventListener('product-form:submit-start', function () {
      // nothing yet
    });

    document.addEventListener('product-form:submit-end', function () {
      fetchCart().then(function (cart) {
        updateDrawer(cart);
        openDrawer();
      });
    });

    // Also watch for Dawn's cart:update
    document.addEventListener('cart:update', function (e) {
      var data = e.detail && e.detail.cartData;
      if (data) {
        updateDrawer(data);
      } else {
        fetchCart().then(updateDrawer);
      }
    });
  }

  // ── Also open on regular ATC buttons with ic-qa-add-btn class ───────────────

  function interceptQuickAddButtons() {
    document.addEventListener('click', function (e) {
      var btn = e.target.closest('.ic-qa-add-btn');
      if (!btn) return;
      // After a short delay fetch and open
      setTimeout(function () {
        fetchCart().then(function (cart) {
          updateDrawer(cart);
          openDrawer();
        });
      }, 800);
    });
  }

  // ── Init ─────────────────────────────────────────────────────────────────────

  function init() {
    drawer = document.getElementById('ic-cart-drawer');
    if (!drawer) return;

    backdrop = document.getElementById('ic-cart-backdrop');
    panel = drawer.querySelector('.ic-cart-drawer__panel');
    closeBtn = document.getElementById('ic-cart-close');
    continueLink = document.getElementById('ic-drawer-continue-link');

    bindDrawerEvents();
    interceptAddToCartForms();
    interceptOverlayButtons();
    interceptQuickAddButtons();
    listenForShopifyEvents();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
